import React, {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { ThemeProvider, useColorScheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { ThemeModeContext } from 'shared/providers/ThemeModeContext';
import { AuthContext } from 'shared/providers/AuthProvider';
import secureFetch from 'shared/lib/secure-fetch';
import { getPreferences, setPreferences } from 'shared/api/user_profile';
import {
  THEME_MODES,
  TIER_OPTIONS,
  LIGHT_THEME_OPTIONS,
  DARK_THEME_OPTIONS,
} from 'shared/constants/theme';
import { createAppTheme } from './theme';
import {
  buildPalette, DEFAULT_LIGHT_THEME, DEFAULT_DARK_THEME, DEFAULT_TIER,
} from './tokens';

const STORAGE_KEY = 'reactor.themeMode';
// Family is stored per scheme; the tier is shared.
const LIGHT_FAMILY_KEY = 'reactor.lightTheme';
const DARK_FAMILY_KEY = 'reactor.darkTheme';
const TIER_KEY = 'reactor.tier';

function readStored(key, options, fallback) {
  try {
    const stored = localStorage.getItem(key);
    return options.some((o) => o.value === stored) ? stored : fallback;
  } catch {
    return fallback;
  }
}

// The theme picker draws each option as that family's own field. Only the active family's
// colours exist as CSS variables, so the others are read from the palette here.
function buildThemeFields(scheme, options, tier) {
  return Object.fromEntries(options.map(({ value }) => {
    const p = buildPalette(scheme, `${value}.${tier}`);
    return [value, { well: p.inputBackground, border: p.borderControl, accent: p.primary.main }];
  }));
}

function hasStored(key) {
  try {
    return localStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

function write(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Persistence is best-effort; the in-memory choice still applies.
  }
}

/**
 * Keeps the theme in the employee's server-side preferences, so it follows them across tabs
 * and browsers; localStorage is only the first-paint cache, and sign-out clears it.
 *
 * On sign-in the server wins. With nothing stored there, values this browser still holds
 * are migrated up; otherwise the theme resets, so a shared workstation doesn't hand the last
 * operator's theme to the next one. Failures stay silent and keep the cached theme.
 */
function useServerPreferences({
  mode, setMode, lightFamily, darkFamily, setFamily, tier, setTier,
}) {
  const { user, getAccessToken } = useContext(AuthContext);
  const userId = user && user.id;
  const current = {
    themeMode: mode || 'system', lightTheme: lightFamily, darkTheme: darkFamily, tier,
  };
  const currentRef = useRef(current);
  currentRef.current = current;
  // The values known to match the server; null until the first load settles.
  const synced = useRef(null);
  // MUI's setMode changes identity after mount; reading it through a ref keeps the load
  // keyed to the user alone.
  const setModeRef = useRef(setMode);
  setModeRef.current = setMode;

  const send = useCallback(async (config) => {
    const token = await getAccessToken();
    return secureFetch(config.path, token, { method: config.method, body: config.body });
  }, [getAccessToken]);

  useEffect(() => {
    synced.current = null;
    if (!userId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const stored = await send(getPreferences(userId));
        if (cancelled) return;
        const valid = {
          themeMode: THEME_MODES.includes(stored.themeMode) ? stored.themeMode : undefined,
          lightTheme: LIGHT_THEME_OPTIONS.some((o) => o.value === stored.lightTheme)
            ? stored.lightTheme : undefined,
          darkTheme: DARK_THEME_OPTIONS.some((o) => o.value === stored.darkTheme)
            ? stored.darkTheme : undefined,
          tier: TIER_OPTIONS.some((o) => o.value === stored.tier) ? stored.tier : undefined,
        };
        const found = Object.fromEntries(Object.entries(valid).filter(([, v]) => v));

        if (Object.keys(found).length) {
          synced.current = { ...currentRef.current, ...found };
          if (found.themeMode) setModeRef.current(found.themeMode);
          if (found.lightTheme) setFamily('light', found.lightTheme);
          if (found.darkTheme) setFamily('dark', found.darkTheme);
          if (found.tier) setTier(found.tier);
          return;
        }

        const local = Object.fromEntries([
          ['themeMode', STORAGE_KEY], ['lightTheme', LIGHT_FAMILY_KEY],
          ['darkTheme', DARK_FAMILY_KEY], ['tier', TIER_KEY],
        ].filter(([, key]) => hasStored(key)).map(([field]) => [field, currentRef.current[field]]));

        if (Object.keys(local).length) {
          synced.current = currentRef.current;
          await send(setPreferences(userId, local));
          return;
        }

        synced.current = {
          themeMode: 'system', lightTheme: DEFAULT_LIGHT_THEME, darkTheme: DEFAULT_DARK_THEME, tier: DEFAULT_TIER,
        };
        setModeRef.current('system');
        setFamily('light', DEFAULT_LIGHT_THEME);
        setFamily('dark', DEFAULT_DARK_THEME);
        setTier(DEFAULT_TIER);
      } catch {
        // Keep the cached theme; later changes still save once a load succeeds.
      }
    })();
    return () => { cancelled = true; };
  }, [userId, send, setFamily, setTier]);

  // Save whatever changed since the last known server state.
  useEffect(() => {
    if (!userId || !synced.current) return;
    const changed = Object.fromEntries(
      Object.entries(current).filter(([field, value]) => synced.current[field] !== value),
    );
    if (!Object.keys(changed).length) return;
    synced.current = { ...synced.current, ...changed };
    send(setPreferences(userId, changed)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, current.themeMode, lightFamily, darkFamily, tier, send]);
}

// Bridges MUI's colour-scheme state to our context. MUI owns the `data-light`/`data-dark`
// attribute; never set it ourselves.
function ThemeModeBridge({
  children, tier, setTier, lightFamily, darkFamily, setFamily, setPreview, themeFields,
}) {
  const { mode, setMode, systemMode } = useColorScheme();
  useServerPreferences({
    mode, setMode, lightFamily, darkFamily, setFamily, tier, setTier,
  });

  // `mode` is 'light' | 'dark' | 'system'; systemMode resolves 'system'.
  const resolvedMode = (mode === 'system' ? systemMode : mode) || 'light';
  const themeFamily = resolvedMode === 'dark' ? darkFamily : lightFamily;

  const value = useMemo(() => ({
    mode: mode || 'system',
    resolvedMode,
    setMode: (next) => {
      if (THEME_MODES.includes(next)) setMode(next);
    },
    toggleMode: () => setMode(resolvedMode === 'dark' ? 'light' : 'dark'),
    themeFamily,
    setThemeFamily: (next) => setFamily(resolvedMode, next),
    // Paints the app in another family without saving it; null puts the saved one back.
    previewThemeFamily: (next) => setPreview(
      next && next !== themeFamily ? { scheme: resolvedMode, family: next } : null,
    ),
    tier,
    setTier,
    themeFields: themeFields[resolvedMode],
  }), [
    mode, resolvedMode, setMode, themeFamily, setFamily, setPreview, tier, setTier, themeFields,
  ]);

  return (
    <ThemeModeContext.Provider value={value}>
      {children}
    </ThemeModeContext.Provider>
  );
}

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
// Owns the theme, the light/dark selection, and the theme family + brightness tier.
export default function ThemeModeProvider({ children }) {
  const [lightFamily, setLightFamily] = useState(
    () => readStored(LIGHT_FAMILY_KEY, LIGHT_THEME_OPTIONS, DEFAULT_LIGHT_THEME),
  );
  const [darkFamily, setDarkFamily] = useState(
    () => readStored(DARK_FAMILY_KEY, DARK_THEME_OPTIONS, DEFAULT_DARK_THEME),
  );
  const [tier, setTierState] = useState(
    () => readStored(TIER_KEY, TIER_OPTIONS, DEFAULT_TIER),
  );
  // { scheme, family } while the picker previews a family; never stored or synced.
  const [preview, setPreviewState] = useState(null);
  const setPreview = useCallback((next) => {
    const options = next && (next.scheme === 'dark' ? DARK_THEME_OPTIONS : LIGHT_THEME_OPTIONS);
    setPreviewState(next && options.some((o) => o.value === next.family) ? next : null);
  }, []);

  const setFamily = useCallback((scheme, next) => {
    setPreviewState(null);
    if (scheme === 'dark') {
      if (!DARK_THEME_OPTIONS.some((o) => o.value === next)) return;
      write(DARK_FAMILY_KEY, next);
      setDarkFamily(next);
      return;
    }
    if (!LIGHT_THEME_OPTIONS.some((o) => o.value === next)) return;
    write(LIGHT_FAMILY_KEY, next);
    setLightFamily(next);
  }, []);

  const setTier = useCallback((next) => {
    if (!TIER_OPTIONS.some((o) => o.value === next)) return;
    write(TIER_KEY, next);
    setTierState(next);
  }, []);

  // Both schemes are built, so a light/dark flip needs no rebuild.
  const shownLight = preview && preview.scheme === 'light' ? preview.family : lightFamily;
  const shownDark = preview && preview.scheme === 'dark' ? preview.family : darkFamily;
  const theme = useMemo(
    () => createAppTheme({
      light: `${shownLight}.${tier}`,
      dark: `${shownDark}.${tier}`,
    }),
    [shownLight, shownDark, tier],
  );

  const themeFields = useMemo(() => ({
    light: buildThemeFields('light', LIGHT_THEME_OPTIONS, tier),
    dark: buildThemeFields('dark', DARK_THEME_OPTIONS, tier),
  }), [tier]);

  return (
    <ThemeProvider
      theme={theme}
      defaultMode="system"
      modeStorageKey={STORAGE_KEY}
    >
      {/* Must be inside ThemeProvider to pick up scheme-aware background/text. */}
      <CssBaseline />
      <ThemeModeBridge
        tier={tier}
        setTier={setTier}
        lightFamily={lightFamily}
        darkFamily={darkFamily}
        setFamily={setFamily}
        setPreview={setPreview}
        themeFields={themeFields}
      >
        {children}
      </ThemeModeBridge>
    </ThemeProvider>
  );
}
